import { useEffect, useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  downloadTaxReliefPdf,
  getClientTaxRelief,
  saveTaxReliefPdf,
  type TaxReliefData,
} from "@/features/reports/api";
import { useActiveClient } from "@/layouts/client-layout";
import { downloadBlob } from "@/lib/download";
import { formatCents } from "@/lib/utils";

type Row = Record<string, unknown>;
const cents = (r: Row, k: string) => formatCents(Number(r[k] ?? 0));
const rows = (section: Record<string, unknown> | undefined): Row[] =>
  (section?.rows as Row[] | undefined) ?? [];

/**
 * Tax Relief Report builder — pick sections, preview the assembled data, then
 * download the branded PDF or save it to the client's Documents.
 */
export default function TaxReliefReportPage() {
  const { client } = useActiveClient();
  const [data, setData] = useState<TaxReliefData | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<"pdf" | "save" | null>(null);

  useEffect(() => {
    getClientTaxRelief(client.id)
      .then((d) => {
        setData(d);
        setSelected(new Set(d.availableSections.map((s) => s.key)));
      })
      .catch((err) =>
        toast.error(err instanceof Error ? err.message : "Could not load the report."),
      );
  }, [client.id]);

  const chosen = useMemo(() => [...selected], [selected]);

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function onDownload() {
    if (chosen.length === 0) return toast.error("Select at least one section.");
    setBusy("pdf");
    try {
      const blob = await downloadTaxReliefPdf(client.id, chosen);
      downloadBlob(`tax-relief-${client.name}.pdf`, blob);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not generate the PDF.");
    } finally {
      setBusy(null);
    }
  }

  async function onSave() {
    if (chosen.length === 0) return toast.error("Select at least one section.");
    setBusy("save");
    try {
      await saveTaxReliefPdf(client.id, chosen);
      toast.success("Tax Relief Report saved to Documents.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save the report.");
    } finally {
      setBusy(null);
    }
  }

  if (!data) return <p className="text-muted-foreground text-sm">Loading…</p>;

  const s = data.sections;
  const has = (k: string) => selected.has(k) && s[k];

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <div className="bg-accent text-primary rounded-full p-2.5">
            <FileText className="size-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Tax Relief Report</h1>
            <p className="text-muted-foreground text-sm">
              Pick the sections to include for <strong>{client.name}</strong>, preview below, then
              download or save the PDF to Documents.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          {data.availableSections.map((sec) => (
            <Label key={sec.key} className="flex items-center gap-2 font-normal">
              <Checkbox checked={selected.has(sec.key)} onCheckedChange={() => toggle(sec.key)} />
              {sec.label}
            </Label>
          ))}
        </div>
        <div className="flex gap-2">
          <Button type="button" onClick={() => void onDownload()} disabled={busy !== null}>
            {busy === "pdf" ? "Generating…" : "Download PDF"}
          </Button>
          <Button type="button" variant="outline" onClick={() => void onSave()} disabled={busy !== null}>
            {busy === "save" ? "Saving…" : "Save to Documents"}
          </Button>
        </div>
      </Card>

      {has("overview") && (
        <Card className="p-5">
          <h2 className="mb-2 font-semibold">Overview</h2>
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Total owed: </span>
              {formatCents(Number(s.overview?.total_owed_cents ?? 0))}
            </div>
            <div>
              <span className="text-muted-foreground">Tax periods: </span>
              {String(s.overview?.record_count ?? 0)}
            </div>
            <div>
              <span className="text-muted-foreground">Open cases: </span>
              {String(s.overview?.open_cases ?? 0)}
            </div>
          </div>
        </Card>
      )}

      {has("tax_owed") && (
        <PreviewTable
          title="Tax Owed"
          headers={["Year", "Form", "Balance", "Interest", "Penalty", "Total"]}
          rows={rows(s.tax_owed).map((r) => [
            String(r.tax_year),
            String(r.form),
            cents(r, "balance_cents"),
            cents(r, "interest_cents"),
            cents(r, "penalty_cents"),
            cents(r, "total_owed_cents"),
          ])}
          footer={`Total owed: ${formatCents(Number(s.tax_owed?.total_owed_cents ?? 0))}`}
        />
      )}

      {has("wage_income") && (
        <PreviewTable
          title="Wage & Income"
          headers={["Year", "Type", "Payer", "Amount"]}
          rows={rows(s.wage_income).map((r) => [
            String(r.tax_year),
            String(r.income_type),
            String(r.payer ?? ""),
            cents(r, "amount_cents"),
          ])}
          footer={`Total income: ${formatCents(Number(s.wage_income?.total_cents ?? 0))}`}
        />
      )}

      {has("payments") && (
        <PreviewTable
          title="Payments"
          headers={["Date", "Year", "Code", "Explanation", "Amount"]}
          rows={rows(s.payments).map((r) => [
            String(r.date ?? "—"),
            String(r.tax_year),
            String(r.code),
            String(r.explanation ?? ""),
            cents(r, "amount_cents"),
          ])}
          footer={`Total payments/credits: ${formatCents(Number(s.payments?.total_cents ?? 0))}`}
        />
      )}

      {has("collection_alternatives") && (
        <Card className="p-5">
          <h2 className="mb-2 font-semibold">Collection Alternatives</h2>
          <p className="text-sm">
            Estimated 72-month installment agreement:{" "}
            <strong>{formatCents(Number(s.collection_alternatives?.ia_72_month_cents ?? 0))}</strong>
            /month on {formatCents(Number(s.collection_alternatives?.total_owed_cents ?? 0))} owed.
          </p>
          <p className="text-muted-foreground mt-1 text-xs italic">
            {String(s.collection_alternatives?.note ?? "")}
          </p>
        </Card>
      )}

      {has("csed") && (
        <PreviewTable
          title="Transcript / CSED"
          headers={["Year", "Form", "Assessed", "CSED", "Days left"]}
          rows={rows(s.csed).map((r) => [
            String(r.tax_year),
            String(r.form),
            String(r.assessment_date ?? "—"),
            String(r.csed_date ?? "—"),
            r.days_remaining == null ? "—" : String(r.days_remaining),
          ])}
        />
      )}
    </div>
  );
}

function PreviewTable({
  title,
  headers,
  rows,
  footer,
}: {
  title: string;
  headers: string[];
  rows: string[][];
  footer?: string;
}) {
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <h2 className="px-4 pt-4 font-semibold">{title}</h2>
      <Table>
        <TableHeader>
          <TableRow>
            {headers.map((h) => (
              <TableHead key={h}>{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} className="text-muted-foreground text-center text-sm">
                No data.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r, i) => (
              <TableRow key={i}>
                {r.map((c, j) => (
                  <TableCell key={j}>{c}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {footer && <p className="px-4 pb-4 pt-2 text-sm font-semibold">{footer}</p>}
    </Card>
  );
}
